import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx, TONE_ICON, TONE_WORD, type BannerTone } from '../internal/tone';
import { IconButton } from '../actions/IconButton';

export interface ToastOptions {
  tone?: BannerTone;
  /** Milissegundos até desaparecer. `0` fica até ela fechar. */
  duration?: number;
}

export interface Toast {
  id: string;
  message: string;
  tone: BannerTone;
  duration: number;
}

interface ToastApi {
  show: (message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  /** Lista atual — útil para testes e para quem quiser desenhar isto de outra forma. */
  toasts: Toast[];
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Duração por omissão.
 *
 * 6 s, não 3: ela lê devagar e o texto é longo. Erros nunca desaparecem
 * sozinhos — ficam até ela fechar.
 */
const DEFAULT_DURATION = 6000;

let counter = 0;

/**
 * Avisos passageiros.
 *
 * REGRA: um toast NUNCA pode ser o único lugar onde uma informação importante
 * aparece. Nada que ela precise de reler pode estar atrás de um temporizador.
 * Para isso existe o `Banner`, que fica na tela. O toast é confirmação de que
 * algo correu bem ("Despesa salva"), não é o registro dessa coisa.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const tone = options.tone ?? 'info';
      // Um erro atrás de um temporizador é um erro perdido.
      const duration =
        options.duration ?? (tone === 'danger' ? 0 : DEFAULT_DURATION);
      counter += 1;
      const id = `toast-${counter}`;

      setToasts((current) => [...current, { id, message, tone, duration }]);

      if (duration > 0) {
        const timer = setTimeout(() => {
          timers.current.delete(id);
          setToasts((current) => current.filter((t) => t.id !== id));
        }, duration);
        timers.current.set(id, timer);
      }

      return id;
    },
    [],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss, toasts }), [show, dismiss, toasts]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="flos-toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={cx('flos-toast', `flos-toast--${toast.tone}`)}>
            <span className="flos-icon" aria-hidden="true">
              {TONE_ICON[toast.tone]}
            </span>
            <span className="flos-toast__text">
              <span className="flos-sr-only">{TONE_WORD[toast.tone]}. </span>
              {toast.message}
            </span>
            <IconButton icon="✕" label="Fechar aviso" onClick={() => dismiss(toast.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Acesso aos avisos. Fora do `ToastProvider` quebra — é erro de montagem. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() precisa de um <ToastProvider> acima na árvore.');
  }
  return ctx;
}
